/**
 * Parent Application Entry Point
 * 
 * This module initializes the parent application and manages the embedded
 * chat interface iframe, including loading states and error handling.
 */

import { getChatIframeUrl, getIframeDimensions } from './config';
import { AuthManager } from './auth/AuthManager';
import { AuthUI } from './auth/AuthUI';

/**
 * ChatIframeManager
 * 
 * Manages the lifecycle of the embedded chat interface iframe, including:
 * - Initialization and configuration
 * - Loading state management
 * - Error handling
 * - Optional cross-origin communication via postMessage
 */
class ChatIframeManager {
  private iframe: HTMLIFrameElement | null = null;
  private loadingIndicator: HTMLElement | null = null;
  private errorMessage: HTMLElement | null = null;
  private loadTimeout: number | null = null;

  constructor(private iframeId: string, private loadingId: string, private errorId: string) {}

  /**
   * Initialize the iframe manager
   * Sets up the iframe with proper configuration and event listeners
   */
  public initialize(): void {
    // Get DOM elements
    this.iframe = document.getElementById(this.iframeId) as HTMLIFrameElement;
    this.loadingIndicator = document.getElementById(this.loadingId);
    this.errorMessage = document.getElementById(this.errorId);

    if (!this.iframe) {
      console.error('Chat iframe element not found');
      return;
    }

    // Configure iframe with URL from config
    const iframeUrl = getChatIframeUrl();
    const dimensions = getIframeDimensions();
    
    // Prevent scroll when setting iframe src
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;
    
    // Set iframe properties
    this.iframe.src = iframeUrl;
    this.iframe.style.height = dimensions.height;
    
    // Restore scroll position immediately
    window.scrollTo(currentScrollX, currentScrollY);
    
    // Also prevent any delayed scroll
    requestAnimationFrame(() => {
      window.scrollTo(currentScrollX, currentScrollY);
    });
    
    console.log(`Initializing chat iframe with URL: ${iframeUrl}`);

    // Set up event listeners for iframe loading
    this.setupEventListeners();

    // Set a timeout for loading (30 seconds)
    // If the iframe doesn't load within this time, show error message
    this.loadTimeout = window.setTimeout(() => {
      this.handleLoadError();
    }, 30000);
  }

  /**
   * Set up event listeners for iframe load events
   */
  private setupEventListeners(): void {
    if (!this.iframe) return;

    // Handle successful iframe load
    this.iframe.addEventListener('load', () => {
      this.handleLoadSuccess();
    });

    // Handle iframe load errors
    this.iframe.addEventListener('error', () => {
      this.handleLoadError();
    });
  }

  /**
   * Handle successful iframe load
   * Hides the loading indicator and clears the timeout
   */
  private handleLoadSuccess(): void {
    console.log('Chat iframe loaded successfully');
    
    // Clear the timeout
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    // Hide loading indicator
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
    }

    // Ensure error message is hidden
    if (this.errorMessage) {
      this.errorMessage.classList.remove('visible');
    }
  }

  /**
   * Handle iframe load error
   * Shows error message and hides loading indicator
   */
  private handleLoadError(): void {
    console.error('Failed to load chat iframe');
    
    // Clear the timeout
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    // Hide loading indicator
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
    }

    // Show error message
    if (this.errorMessage) {
      this.errorMessage.classList.add('visible');
    }

    // Hide the iframe since it failed to load
    if (this.iframe) {
      this.iframe.style.display = 'none';
    }
  }

  /**
   * Set up postMessage listener for cross-origin communication
   * This is optional and can be used if the chat interface needs to
   * communicate with the parent application
   */
  public setupMessageListener(): void {
    window.addEventListener('message', (event) => {
      // SECURITY: Always verify the origin of messages
      const allowedOrigins = [
        'http://localhost:5173',
        'http://localhost:5174',
        'https://dam-chatbot.aws-innovations-dev-au.iress.online',
        'https://dam.aws-innovations-dev-au.iress.online'
      ];

      if (!allowedOrigins.includes(event.origin)) {
        console.warn('Received message from unauthorized origin:', event.origin);
        return;
      }

      // Handle messages from the chat interface
      console.log('Received message from chat interface:', event.data);
      
      // Example: Handle different message types
      if (event.data.type === 'chat-ready') {
        console.log('Chat interface is ready');
      } else if (event.data.type === 'chat-error') {
        console.error('Chat interface error:', event.data.error);
      }
    });
  }

  /**
   * Send a message to the iframe (optional)
   * This can be used to communicate with the chat interface
   */
  public sendMessage(message: any): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.error('Cannot send message: iframe not available');
      return;
    }

    const iframeUrl = getChatIframeUrl();
    const targetOrigin = new URL(iframeUrl).origin;
    
    this.iframe.contentWindow.postMessage(message, targetOrigin);
  }

  /**
   * Send authentication token to the iframe
   */
  public sendAuthToken(token: string | null): void {
    this.sendMessage({
      type: 'auth-token',
      token: token
    });
  }
}

/**
 * Initialize the application when DOM is ready
 */
async function initializeApp(): Promise<void> {
  console.log('Initializing parent application...');

  // Initialize authentication
  let authManager: AuthManager;
  let authUI: AuthUI;
  
  try {
    authManager = new AuthManager();
    authUI = new AuthUI(authManager);
  } catch (error) {
    console.error('Failed to initialize authentication:', error);
    // Show error in the auth container
    const authContainer = document.getElementById('auth-container');
    if (authContainer) {
      authContainer.innerHTML = '<div style="color: red; padding: 8px;">Auth configuration error. Check console.</div>';
    }
    return;
  }

  // Check if this is an OAuth callback
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('code')) {
    try {
      await authManager.handleCallback();
      // Continue to render the app after successful auth
    } catch (error) {
      console.error('OAuth callback error:', error);
      // Continue to render the app even if callback fails
    }
  }

  // Check authentication status
  if (!authManager.isAuthenticated()) {
    // User not authenticated - show login screen
    showLoginScreen(authManager);
    return;
  }

  // User is authenticated - show the portal
  // Render auth status in header
  const authContainer = document.getElementById('auth-container');
  if (authContainer) {
    authUI.renderAuthStatus(authContainer);
  }

  // Initialize the chat iframe manager
  const chatManager = new ChatIframeManager(
    'chat-interface',
    'iframe-loading',
    'iframe-error'
  );

  // Lock scroll position during iframe initialization
  const initialScrollY = window.scrollY;
  const initialScrollX = window.scrollX;
  
  const lockScroll = () => {
    window.scrollTo(initialScrollX, initialScrollY);
  };
  
  // Add scroll listener to prevent any scroll during initialization
  window.addEventListener('scroll', lockScroll);
  
  chatManager.initialize();
  
  // Remove scroll lock after a short delay
  setTimeout(() => {
    window.removeEventListener('scroll', lockScroll);
  }, 1000);
  
  // Send auth token to iframe after it loads and prevent parent scroll
  const iframe = document.getElementById('chat-interface') as HTMLIFrameElement;
  if (iframe) {
    // Prevent auto-scroll when iframe loads
    const currentScrollPosition = window.scrollY;
    
    // Restore scroll position if iframe causes scroll
    const preventScroll = () => {
      if (window.scrollY !== currentScrollPosition) {
        window.scrollTo(0, currentScrollPosition);
      }
    };
    
    iframe.addEventListener('load', preventScroll, { once: true });
    
    // Also prevent during initialization
    setTimeout(preventScroll, 100);
    // Prevent scroll events from bubbling to parent
    iframe.addEventListener('wheel', (e) => {
      e.stopPropagation();
    }, { passive: false });

    // Prevent touch scroll on mobile
    iframe.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    }, { passive: false });

    // When iframe is focused, prevent parent scroll
    iframe.addEventListener('mouseenter', () => {
      document.body.style.overflow = 'hidden';
    });

    iframe.addEventListener('mouseleave', () => {
      document.body.style.overflow = '';
    });

    // Send auth token after iframe loads
    iframe.addEventListener('load', () => {
      // Wait a bit for the iframe to be ready
      setTimeout(() => {
        const token = authManager.getAccessToken();
        chatManager.sendAuthToken(token);
      }, 500);
    });
  }
  
  // Optional: Set up message listener for cross-origin communication
  // Uncomment if you need bidirectional communication with the chat interface
  // chatManager.setupMessageListener();

  // Add keyboard navigation support for service cards
  setupServiceCardNavigation();
}

/**
 * Show login screen when user is not authenticated
 */
function showLoginScreen(authManager: AuthManager): void {
  // Hide the main content
  const mainContent = document.querySelector('main');
  if (mainContent) {
    mainContent.style.display = 'none';
  }

  // Hide the header
  const header = document.querySelector('header');
  if (header) {
    header.style.display = 'none';
  }

  // Hide the breadcrumb
  const breadcrumb = document.querySelector('.breadcrumb');
  if (breadcrumb) {
    (breadcrumb as HTMLElement).style.display = 'none';
  }

  // Create and show login screen
  const loginScreen = document.createElement('div');
  loginScreen.id = 'login-screen';
  loginScreen.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  `;

  loginScreen.innerHTML = `
    <div style="
      background: white;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      padding: 48px;
      max-width: 450px;
      width: 90%;
      text-align: center;
    ">
      <div style="font-size: 48px; margin-bottom: 24px;">🔷</div>
      <h1 style="
        font-size: 32px;
        font-weight: 700;
        color: #1a202c;
        margin-bottom: 16px;
      ">Welcome to IFA Portal</h1>
      <p style="
        font-size: 16px;
        color: #718096;
        margin-bottom: 32px;
        line-height: 1.6;
      ">
        Sign in to access your dedicated support portal and Digital Dan, your always on Account Manager
      </p>
      <button id="login-button" style="
        width: 100%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-size: 16px;
        font-weight: 600;
        padding: 16px 32px;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: transform 0.2s, box-shadow 0.2s;
      " onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 8px 20px rgba(102, 126, 234, 0.4)'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='none'">
        Sign In with Cognito
      </button>
    </div>
  `;

  document.body.appendChild(loginScreen);

  // Add click handler to login button
  const loginButton = document.getElementById('login-button');
  if (loginButton) {
    loginButton.addEventListener('click', () => {
      authManager.login();
    });
  }
}

/**
 * Set up keyboard navigation for service cards
 * Allows users to activate cards with Enter or Space keys
 */
function setupServiceCardNavigation(): void {
  const serviceCards = document.querySelectorAll('.service-card');
  
  serviceCards.forEach((card) => {
    card.addEventListener('keydown', (event) => {
      const keyEvent = event as KeyboardEvent;
      
      // Activate card on Enter or Space
      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
        keyEvent.preventDefault();
        (card as HTMLElement).click();
      }
    });

    // Add click handler (placeholder - can be customized)
    card.addEventListener('click', () => {
      const title = card.querySelector('.service-title')?.textContent;
      console.log(`Service card clicked: ${title}`);
      // In a real application, this would navigate to a form or open a modal
    });
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}
